//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React, { useState } from 'react';
import { RegisterOptions, useFormContext } from 'react-hook-form';
import styled, { css } from 'styled-components';

import { UploadTab } from '@campaigns/components/shared/product-details-modal/drawer/variants/image-preview-popover/tabs/UploadTab';
import { UrlTab } from '@campaigns/components/shared/product-details-modal/drawer/variants/image-preview-popover/tabs/UrlTab';
import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import { useProductModal } from '@campaigns/contexts/ProductModalContext';
import { EditableBVSamplingProduct } from '@campaigns/types/sampling/SamplingProductTypes';
import { PrimaryLinkButton } from '@components/atoms/button/Button';
import { CaptionText } from '@components/atoms/text/Text';
import { SUPPORTED_IMAGE_EXTENSIONS } from '@utils/mimeTypes';
import { toGrammaticallyCorrectList } from '@utils/stringUtils';

export interface VariantImageUploadPopoverProps {
  index: number;
  onClearImage: () => void;
  onClosePopover: () => void;
  onFileSelect: (file: File | undefined) => void;
  onPreview: () => void;
  onRevokePreviousImage: (previousImgUrl: string | undefined) => void;
  rules: RegisterOptions | undefined;
}

export const VariantImageUploadPopover = ({
  index,
  onClearImage,
  onClosePopover,
  onFileSelect,
  onPreview,
  onRevokePreviousImage,
  rules,
}: VariantImageUploadPopoverProps) => {
  const { mainProductImage } = useProductModal();
  const { getValues, setValue } = useFormContext<EditableBVSamplingProduct>();
  const [isImageUrlInputMode, setIsImageUrlInputMode] = useState(true);

  const handleUseMainProductImage = () => {
    const currentMainImgUrl = getValues('imgUrl');

    // If we have a current main image URL, use that
    if (currentMainImgUrl) {
      if (currentMainImgUrl.startsWith('blob:') && mainProductImage?.file) {
        // Use the main product image file for this variant
        onFileSelect(mainProductImage.file);
        onClosePopover();
      } else {
        // For regular URLs
        const previousImgUrl = getValues(`variations.${index}.imgUrl`);
        onRevokePreviousImage(previousImgUrl);
        setValue(`variations.${index}.imgUrl`, currentMainImgUrl, {
          shouldValidate: true,
        });
        onFileSelect(undefined);
        onClosePopover();
        onPreview();
      }
    }
  };

  return (
    <PopoverContent>
      <StyledPrimaryLinkButton
        onClick={handleUseMainProductImage}
        trackingId={
          SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_ADD_VARIANT_IMAGE_USE_MAIN_PRODUCT_IMAGE_BUTTON
        }
      >
        Use parent image
      </StyledPrimaryLinkButton>
      <ImageGroup>
        <CaptionWrapper>
          <CaptionText>
            File types supported:{' '}
            {toGrammaticallyCorrectList(SUPPORTED_IMAGE_EXTENSIONS)}
          </CaptionText>
        </CaptionWrapper>
        {isImageUrlInputMode ? (
          <UrlTab
            index={index}
            onFileSelect={onFileSelect}
            onPreview={onPreview}
            rules={rules}
          />
        ) : (
          <UploadTab
            index={index}
            onClearImage={onClearImage}
            onClosePopover={onClosePopover}
            onFileSelect={onFileSelect}
            rules={rules}
          />
        )}
        <ToggleContainer>
          <CaptionText>or</CaptionText>
          {isImageUrlInputMode ? (
            <PrimaryLinkButton
              onClick={() => setIsImageUrlInputMode(false)}
              trackingId={
                SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_DRAWER_VARIANT_UPLOAD_IMAGE_TOGGLE_BUTTON
              }
            >
              Upload image
            </PrimaryLinkButton>
          ) : (
            <PrimaryLinkButton
              onClick={() => setIsImageUrlInputMode(true)}
              trackingId={
                SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_DRAWER_VARIANT_IMAGE_URL_TOGGLE_BUTTON
              }
            >
              Enter image link instead
            </PrimaryLinkButton>
          )}
        </ToggleContainer>
      </ImageGroup>
    </PopoverContent>
  );
};

const PopoverContent = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.echo.spacing.xxs};
    padding: ${theme.spacings[3]};
    width: ${theme.mediumMeasure};
  `}
`;

const StyledPrimaryLinkButton = styled(PrimaryLinkButton)`
  align-self: flex-start;
`;

const ImageGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.echo.spacing.xs};
`;

const CaptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[1]};
`;

const ToggleContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.echo.spacing.xxs};
`;
